import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { getCategoryTabId } from '@client/redux/modules/mylounge';
import { CategoryTabIds } from '@shared/constants';
import { getCookie } from '@pegasus/utils';
import { useVariant } from '@pegasus/abtesting';

import { ContinueShoppingData } from './types';
import { mockContinueShoppingData } from './mock-data';
import { MINIMUM_PRODUCTS_REQUIRED } from './constants';

export interface UseContinueShoppingReturn {
    shouldShowContinueShopping: boolean;
    continueShoppingData: ContinueShoppingData;
}

/**
 * Custom hook for managing Continue Shopping functionality
 *
 * @returns {UseContinueShoppingReturn} Object containing:
 * - isContinueShoppingVariant: Whether the CONTINUE_SHOPPING variant is active
 * - shouldShowContinueShopping: Whether to show the Continue Shopping carousel on the All tab
 * - continueShoppingData: Data for the carousel (categories with products)
 */
const hasMinimumProductsRequired = (data: ContinueShoppingData): boolean => {
    return data.some(category => category.configs.length >= MINIMUM_PRODUCTS_REQUIRED);
};

const shouldDisplayCarousel = (
    isEnabled: boolean,
    tabId: string | null,
    hasProducts: boolean,
): boolean => {
    return isEnabled && tabId === CategoryTabIds.ALL && hasProducts;
};

export const useContinueShopping = (): UseContinueShoppingReturn => {
    const categoryTabId = useSelector(getCategoryTabId);
    const isVariantActive = useVariant(
        'activate_continue_where_you_left',
        'variant_enable_continue_shop',
    );
    const isCookieEnabled = getCookie('isContinueShopping') === 'true';

    const isEnabled = useMemo(() => {
        return Boolean(isVariantActive || isCookieEnabled);
    }, [isVariantActive, isCookieEnabled]);

    // TODO: Replace with actual Redux selector when backend is ready
    // const continueShoppingData = useSelector(getContinueShoppingData) || [];
    const continueShoppingData: ContinueShoppingData = mockContinueShoppingData;

    const shouldShowContinueShopping = useMemo(() => {
        if (!isEnabled) {
            return false;
        }

        const hasProducts = hasMinimumProductsRequired(continueShoppingData);
        return shouldDisplayCarousel(isEnabled, categoryTabId, hasProducts);
    }, [isEnabled, categoryTabId, continueShoppingData]);

    return useMemo(
        () => ({
            shouldShowContinueShopping,
            continueShoppingData: isEnabled ? continueShoppingData : [],
        }),
        [shouldShowContinueShopping, continueShoppingData, isEnabled],
    );
};
